import moment from "moment";

import type pino from "pino";

import { type LoggerLevel, LoggerEnvs } from "../constants";
import { TRANSFORMED_LOGGER_CONTEXT_KEYS } from "../context";

const removeKeys = (o: object, keys: string[]) => {
  for (let i = 0; i < keys.length; i++) {
    delete o[keys[i] as keyof typeof o];
  }
  return o;
};

const LevelConsoleMethods: {
  [key in Exclude<LoggerLevel, "silent">]: Console[LoggerLevel & keyof Console];
} = {
  /* eslint-disable-next-line no-console */
  debug: console.debug,
  /* eslint-disable-next-line no-console */
  info: console.info,
  /* eslint-disable-next-line no-console */
  warn: console.warn,
  /* eslint-disable-next-line no-console */
  error: console.error,
};

const createBrowserWriteMethod =
  ({ level }: { level: Exclude<LoggerLevel, "silent"> }) =>
  (o: object) => {
    let stringParts: string[] = [];
    if ("time" in o && typeof o.time === "number") {
      stringParts = [
        ...stringParts,
        `[${moment(o.time).format("YYYY-MM-DD HH:mm:ss")}]`,
      ];
      delete o.time;
    }
    if ("module" in o && typeof o.module === "string") {
      stringParts = [...stringParts, `[${o.module}]`];
    }
    if ("env" in o && LoggerEnvs.contains(o.env)) {
      stringParts = [...stringParts, `[${o.env}]`];
    }
    if ("msg" in o && typeof o.msg === "string") {
      stringParts = [
        ...stringParts.slice(0, stringParts.length - 1),
        `${stringParts[stringParts.length - 1]}:`,
        o.msg,
      ];
      delete o.msg;
    }
    /* In a browser context, the environment will almost certainly be development.  In this case,
       we do not need all of the additional context that is automatically included by the Logger
       class - only the context that is explicitly provided to the Logger's associated method.  The
       other, auto-injected context is only useful when looking at logs in a terminal or stream
       in non-development environments. */
    o = removeKeys(o, [...TRANSFORMED_LOGGER_CONTEXT_KEYS, "level"]);
    if (Object.keys(o).length !== 0) {
      LevelConsoleMethods[level](o, stringParts.join(" "));
    } else {
      LevelConsoleMethods[level](stringParts.join(" "));
    }
  };

export const BrowserWriteConfig: Record<
  pino.Level & LoggerLevel,
  pino.WriteFn
> = {
  info: createBrowserWriteMethod({ level: "info" }),
  error: createBrowserWriteMethod({ level: "error" }),
  warn: createBrowserWriteMethod({ level: "warn" }),
  debug: createBrowserWriteMethod({ level: "debug" }),
};
